/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {performBulkOperation} from '@ion/core/src/services/Connect';
import {BulkEntityOperation} from '@ion/core/src/types/entities';

export interface UseBulkOperationParams extends BulkEntityOperation {};

export type UseBulkOperationResult = {
  process: (params: BulkEntityOperation, callbacks) => void;
  isProcessing: boolean;
}

/**
 * Hook for calling the bulk operation api for bulk entity actions.
 *
 * ```typescript
 *
 *  import {useBulkOperation} from '@ion/packages/query/src/hooks';
 *
 *  const {process, isProcessing} = useBulkOperation({
 *    bulkOperation : {
 *      actionType,
 *      actionDetails
 *    },
 *    entityIdentifierS3Path,
 *    entityIdentifierType,
 *    accountId,
 *    hierLevel,
 *    entityType,
 *    searchCriteria,
 *    taskNickName
 *  });
 *
 * ```
 */
export const useBulkOperation = (): UseBulkOperationResult => {
  const {mutate, isLoading} = useMutation(async({
    entityIdentifierType,
    entityIdentifierList,
    entityIdentifierS3Path,
    searchCriteria,
    entityType,
    taskNickName,
    bulkOperation,
    hierLevel,
    isProvisioning,
    search,
    currRegion,
    accountId
  }: BulkEntityOperation) => {
    const hasFile = !!entityIdentifierS3Path?.length;
    const hasList = !!entityIdentifierList?.length;
    const hasSearch = !hasList && !hasFile;
    const operation: BulkEntityOperation = {
      entityIdentifierType : (hasFile || hasList) ? entityIdentifierType : undefined,
      entityIdentifierList : (!hasFile && !hasSearch) ? entityIdentifierList : undefined,
      accountId,
      entityType,
      taskNickName,
      bulkOperation
    };
    if (!isProvisioning) {
      operation.entityIdentifierS3Path = hasFile ? entityIdentifierS3Path : undefined;
      operation.searchCriteria = hasSearch ? searchCriteria : undefined;
      operation.hierLevel = hierLevel;
    } else {
      operation.search = hasSearch ? search : undefined;
      operation.currRegion = currRegion;
    }
    return await performBulkOperation({operation, isProvisioning});
  });

  return {
    process      : mutate,
    isProcessing : isLoading
  };
};

export default useBulkOperation;
